import { NavLink, Outlet, useMatch } from "react-router-dom";
import {
  Boxes,
  Building,
  ExternalLink,
  LayoutDashboard,
  LayoutTemplate,
  ScrollText,
  ShieldCheck,
  Trash2,
  UserCog,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCatalog } from "@/context/CatalogContext";
import AccessDenied from "@/components/AccessDenied";

/**
 * Central Administrativa — um único lugar para empresas, usuários, acessos,
 * catálogo e governança. As seções aparecem conforme o Catálogo de Módulos
 * (decisão do backend); configurações globais específicas continuam em suas
 * páginas próprias e são apenas atalhos aqui.
 */
const SECTIONS = [
  { to: "/admin", label: "Visão geral", icon: LayoutDashboard, end: true, any: ["gestao_acessos", "empresas", "catalogo_modulos"] },
  // CENTRAL ÚNICA DE PERMISSÕES — primeiro item: é o ponto de entrada
  // canônico para conceder/revogar módulos, ações, contratos e perfis.
  { to: "/admin/permissoes", label: "Central de Permissões", icon: ShieldCheck, any: ["gestao_acessos", "empresas", "catalogo_modulos"] },
  { to: "/admin/empresas", label: "Empresas", icon: Building, entry: "empresas" },
  { to: "/admin/usuarios", label: "Usuários e acessos", icon: Users, entry: "gestao_acessos" },
  { to: "/admin/perfis", label: "Perfis", icon: UserCog, entry: "gestao_acessos" },
  { to: "/admin/catalogo", label: "Catálogo de módulos", icon: Boxes, entry: "catalogo_modulos" },
  { to: "/admin/dashboards", label: "Design de dashboards", icon: LayoutTemplate, entry: "design_dashboard" },
  { to: "/ocorrencias/lixeira", label: "Lixeira de ocorrências", icon: Trash2, entry: "ocorrencias", action: ["ocorrencias", "restore"] },
  { to: "/auditoria", label: "Auditoria", icon: ScrollText, entry: "auditoria" },
];

const GLOBAL_SETTINGS = [
  "administracao_plataforma",
  "administracao_sistema",
  "configuracao_operacional",
  "identidade_visual_global",
  "administracao_dados",
];

export default function AdminLayout() {
  const { can } = useAuth();
  const { isAvailable, byKey, loaded } = useCatalog();
  // O editor de dashboard é um espaço de trabalho: ocupa a largura toda
  // (o editor tem o próprio "voltar" para a lista).
  const editingDashboard = useMatch("/admin/dashboards/:dashboardId");

  const visible = SECTIONS.filter((section) => {
    if (section.any) return section.any.some((key) => isAvailable(key));
    if (!isAvailable(section.entry)) return false;
    return section.action ? can(section.action[0], section.action[1]) : true;
  });
  const shortcuts = GLOBAL_SETTINGS.filter((key) => isAvailable(key)).map((key) => byKey[key]);

  if (loaded && visible.length === 0) {
    return <AccessDenied title="Central Administrativa" reason="Seu perfil não administra empresas, usuários nem o catálogo." />;
  }

  if (editingDashboard) {
    return (
      <div className="mx-auto w-full max-w-[1600px]" data-testid="admin-center">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 lg:flex-row" data-testid="admin-center">
      <aside className="lg:w-60 shrink-0" aria-label="Seções da Central Administrativa">
        <div className="rounded-xl border border-border bg-card p-3 lg:sticky lg:top-4">
          <p className="flex items-center gap-2 px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <ShieldCheck size={14} className="text-primary" /> Central Administrativa
          </p>
          <nav className="flex gap-1 overflow-x-auto lg:flex-col" data-testid="admin-nav">
            {visible.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`
                }
                data-testid={`admin-nav-${to.split("/").pop() || "overview"}`}
              >
                <Icon size={16} aria-hidden="true" /> {label}
              </NavLink>
            ))}
          </nav>
          {shortcuts.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Configurações globais
              </p>
              {shortcuts.map((entry) => (
                <NavLink
                  key={entry.key}
                  to={entry.route}
                  className="flex items-center justify-between gap-2 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                  data-testid={`admin-shortcut-${entry.key}`}
                >
                  <span className="truncate">{entry.label}</span>
                  <ExternalLink size={12} aria-hidden="true" />
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </aside>
      <section className="min-w-0 flex-1 space-y-6">
        <Outlet />
      </section>
    </div>
  );
}
